/**
 * Fable 검증 상태 페이지 데이터 로딩 훅.
 * 소스별로 성공/실패를 분리 보관해, 일부 API가 실패해도 나머지는 표시한다.
 */
import { useCallback, useEffect, useState } from 'react';
import { auditCohortId } from '../components/atu2fable/fableLabels';
import type {
  DailyTacticalBlockerAlert,
  DailyTacticalOpsReport,
  DailyTacticalImprovementProposals,
  DailyTacticalReportHistory,
  DailyTacticalReadiness,
  DailyTacticalWeeklyReview,
} from '../services/operationsApi';
import {
  DtsSummary,
  DtsWeeklyAudit,
  fetchFableBlockerAlert,
  fetchFableImprovementProposals,
  fetchFableMarketFallback,
  fetchFableOpsReport,
  fetchFableOpsReportHistory,
  fetchFableOverview,
  fetchFableReadiness,
  fetchFableSummary,
  fetchFableWeeklyAudit,
  fetchFableWeeklyReview,
} from '../services/fableLabApi';

export interface Slice<T> {
  data: T | null;
  error: string | null;
}

export interface FableMarketView {
  isOpen: boolean;
  statusText: string;
  nextOpen: string | null;
  nextClose: string | null;
  nextEventText: string | null;
}

export interface FableOpsAutomationView {
  opsReport: DailyTacticalOpsReport;
  weeklyReview: DailyTacticalWeeklyReview;
  blockerAlert: DailyTacticalBlockerAlert;
}

const EMPTY: Slice<never> = { data: null, error: null };

async function wrap<T>(promise: Promise<T>): Promise<Slice<T>> {
  try {
    return { data: await promise, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : String(err) };
  }
}

const MARKET_STATUS_TEXT: Record<string, string> = {
  open: '미국장 열림',
  closed_regular: '미국장 닫힘 (정규장 외 시간)',
  closed_holiday: '미국장 닫힘 (휴장일)',
  unknown: '미국장 상태 확인 불가',
};

async function loadMarket(): Promise<Slice<FableMarketView>> {
  const overview = await wrap(fetchFableOverview());
  if (overview.data) {
    const m = overview.data.market;
    return {
      data: {
        isOpen: m.is_open,
        statusText: MARKET_STATUS_TEXT[m.status] || m.status,
        nextOpen: m.next_open,
        nextClose: m.next_close,
        nextEventText: null,
      },
      error: null,
    };
  }
  const fallback = await wrap(fetchFableMarketFallback());
  if (fallback.data) {
    const f = fallback.data;
    return {
      data: {
        isOpen: f.is_open,
        statusText: f.status_text,
        nextOpen: null,
        nextClose: null,
        nextEventText: f.next_event,
      },
      error: null,
    };
  }
  return { data: null, error: fallback.error || overview.error };
}

async function loadOpsAutomation(cohortId: string): Promise<FableOpsAutomationView> {
  const [opsReport, weeklyReview, blockerAlert] = await Promise.all([
    fetchFableOpsReport(cohortId),
    fetchFableWeeklyReview(cohortId),
    fetchFableBlockerAlert(cohortId),
  ]);
  return { opsReport, weeklyReview, blockerAlert };
}

export function useFableLab() {
  const [readiness, setReadiness] = useState<Slice<DailyTacticalReadiness>>(EMPTY);
  const [summary, setSummary] = useState<Slice<DtsSummary>>(EMPTY);
  const [market, setMarket] = useState<Slice<FableMarketView>>(EMPTY);
  const [audit, setAudit] = useState<Slice<DtsWeeklyAudit>>(EMPTY);
  const [opsAutomation, setOpsAutomation] = useState<Slice<FableOpsAutomationView>>(EMPTY);
  const [reportHistory, setReportHistory] = useState<Slice<DailyTacticalReportHistory>>(EMPTY);
  const [proposals, setProposals] = useState<Slice<DailyTacticalImprovementProposals>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, s, m] = await Promise.all([
      wrap(fetchFableReadiness(20)),
      wrap(fetchFableSummary(5)),
      loadMarket(),
    ]);
    const cohortId = r.data ? auditCohortId(r.data) : null;
    const [a, ops, history, improvementProposals] = cohortId
      ? await Promise.all([
        wrap(fetchFableWeeklyAudit(cohortId)),
        wrap(loadOpsAutomation(cohortId)),
        wrap(fetchFableOpsReportHistory(cohortId)),
        wrap(fetchFableImprovementProposals(cohortId)),
      ])
      : Array(4).fill({ data: null, error: r.error });
    setReadiness(r);
    setSummary(s);
    setMarket(m);
    setAudit(a);
    setOpsAutomation(ops);
    setReportHistory(history);
    setProposals(improvementProposals);
    setRefreshedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    readiness, summary, market, audit, opsAutomation, reportHistory, proposals,
    loading, refreshedAt, reload: load,
  };
}
